import Papa from 'papaparse'

export type ParsedCsv = {
  headers: string[]
  rows: string[][]
}

export class CsvParseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CsvParseError'
  }
}

export type ParseCsvFile = (file: File) => Promise<ParsedCsv>

const REPLACEMENT_CHAR = '�'

function stripBom(value: string): string {
  return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value
}

export function parseCsvText(text: string): ParsedCsv {
  const result = Papa.parse<string[]>(stripBom(text), {
    skipEmptyLines: 'greedy',
  })

  if (result.errors.length > 0) {
    const first = result.errors[0]
    throw new CsvParseError(first.message || 'The CSV file could not be parsed.')
  }

  const data = result.data
  if (data.length === 0) {
    throw new CsvParseError('The CSV file is empty.')
  }

  const headers = data[0].map((h) => h.trim())
  if (headers.length === 0 || headers.every((h) => h === '')) {
    throw new CsvParseError('The CSV file is missing a header row.')
  }

  const rows = data.slice(1)
  const raggedIndex = rows.findIndex((row) => row.length !== headers.length)
  if (raggedIndex !== -1) {
    throw new CsvParseError(
      `Row ${raggedIndex + 2} has ${rows[raggedIndex].length} columns but the header has ${headers.length}. The file was not imported.`,
    )
  }

  return { headers, rows }
}

export const parseCsvFile: ParseCsvFile = async (file) => {
  let text: string
  try {
    text = await file.text()
  } catch {
    throw new CsvParseError('The file could not be read.')
  }

  if (text.includes(REPLACEMENT_CHAR)) {
    throw new CsvParseError('The file is not valid UTF-8 text.')
  }

  return parseCsvText(text)
}
